import React from 'react';
import { FiCpu, FiLayers, FiImage, FiZap, FiTarget, FiShield, FiTrendingUp } from 'react-icons/fi';
import usePageMeta from '../shared/hooks/usePageMeta';
import { SUPPORT_EMAIL } from '../shared/constants/contactInfo';

const Section = ({ title, children, className = "" }) => (
    <section className={`py-20 px-4 sm:px-6 relative ${className}`}>
        <div className="max-w-5xl mx-auto">
             {title && (
                 <div className="flex items-center gap-4 mb-12">
                    <span className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10"></span>
                    <h2 className="text-2xl font-black text-cream uppercase tracking-tight">{title}</h2>
                    <span className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10"></span>
                 </div>
             )}
             {children}
        </div>
    </section>
);

const PipelineStage = ({ step, icon, title, description }) => (
    <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-slate-blue/20 to-matcha/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
        <div className="relative p-8 bg-warm-gray/20 border border-white/5 rounded-2xl h-full hover:border-slate-blue/30 transition-colors">
            <div className="text-xs font-black text-cream/20 uppercase tracking-widest mb-4">Stage 0{step}</div>
            <div className="w-12 h-12 bg-warm-gray/20 rounded-lg flex items-center justify-center text-slate-blue mb-4">
                {icon}
            </div>
            <h3 className="text-lg font-bold text-cream uppercase mb-3">{title}</h3>
            <p className="text-sm text-cream/60 leading-relaxed font-light">{description}</p>
        </div>
    </div>
);

const PersonaCard = ({ icon, title, traits }) => (
    <div className="p-6 bg-warm-gray/20 border border-white/5 rounded-2xl hover:border-white/15 transition-colors">
        <div className="text-3xl mb-4">{icon}</div>
        <h3 className="text-lg font-bold text-cream uppercase mb-4">{title}</h3>
         <ul className="space-y-2">
            {traits.map((trait, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-cream/60">
                    <span className="text-slate-blue mt-1">&bull;</span>
                    {trait}
                </li>
            ))}
         </ul>
    </div>
);

const About = () => {
    usePageMeta('About', 'Learn about NextAInsight — AI-powered social media content automation by hellAIdea Ltd.');

    return (
        <div className="text-cream">
            <div className="pt-8">

                {/* Hero */}
                <section className="text-center px-4 max-w-4xl mx-auto mb-20 animate-fade-in-up">
                    <h1 className="text-4xl md:text-6xl font-black text-cream uppercase tracking-tighter mb-6">
                        Revolutionizing <br/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-blue to-matcha">Social Automation</span>
                    </h1>
                    <p className="text-xl text-cream/50 leading-relaxed font-light">
                        NextAInsight operates at the intersection of creative strategy and machine intelligence, empowering brands to dominate the feed.
                    </p>
                </section>

                {/* Mission */}
                <Section className="bg-warm-gray/5 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl font-black text-cream uppercase tracking-tight mb-6">Our Mission</h2>
                            <div className="space-y-4 text-cream/70 leading-relaxed font-light">
                                <p>
                                    We built NextAInsight to solve a fundamental paradox: audience appetite for high-quality content is infinite, but human creative capacity is finite.
                                </p>
                                <p>
                                    We believe every brand deserves professional-grade visual storytelling without the agency-grade budget. By combining state-of-the-art generative models with disciplined marketing frameworks, we've created a platform that turns "one post a day" into an automated reality.
                                </p>
                            </div>
                        </div>
                         <div className="relative">
                            <div className="relative bg-warm-dark border border-white/10 p-8 rounded-2xl shadow-2xl skew-y-3 md:skew-y-6 transform hover:skew-y-0 transition-transform duration-500">
                                <div className="flex items-center gap-3 mb-4 border-b border-white/5 pb-4">
                                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                </div>
                                <div className="space-y-3 font-mono text-xs text-cream/50">
                                    <p className="text-cream"><span className="text-slate-blue">&gt;</span> Initializing creative_core...</p>
                                    <p><span className="text-green-400">&#10003;</span> Visual styles loaded</p>
                                    <p><span className="text-green-400">&#10003;</span> Trend analysis complete</p>
                                    <p className="text-cream"><span className="text-slate-blue">&gt;</span> Generating masterpiece...</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>

                {/* Technology Pipeline */}
                <Section title="The Technology">
                    <div className="grid md:grid-cols-3 gap-6">
                        <PipelineStage
                            step="1"
                            icon={<FiCpu />}
                            title="The Architect"
                            description="Analyzes your minimal prompt and expands it into a comprehensive visual blueprint, selecting lighting, composition, and mood."
                        />
                        <PipelineStage
                            step="2"
                            icon={<FiLayers />}
                            title="The Synthesizer"
                            description="Converts the blueprint into precise generation parameters, optimizing for platform specific aspect ratios and styles."
                        />
                        <PipelineStage
                            step="3"
                            icon={<FiImage />}
                            title="The Renderer"
                            description="Uses our diffusion pipeline to manifest the final high-fidelity asset, ready for instant social deployment."
                        />
                    </div>
                </Section>

                {/* Who It's For */}
                <Section title="Who It's For" className="bg-warm-gray/5">
                    <div className="grid md:grid-cols-3 gap-6">
                        <PersonaCard
                            icon="&#128188;"
                            title="Marketing Pros"
                            traits={["Manage multiple brands", "Consistent schedules", "Scale without headcount"]}
                        />
                        <PersonaCard
                            icon="&#10024;"
                            title="Creators"
                            traits={["Focus on strategy", "Automate repetition", "Experiment with styles"]}
                        />
                         <PersonaCard
                            icon="&#128640;"
                            title="Small Business"
                            traits={["Agency quality visuals", "Set & Forget automation", "Cost effective scaling"]}
                        />
                    </div>
                </Section>

                {/* Values */}
                <Section>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        <div className="group">
                             <FiShield className="mx-auto text-3xl text-cream/30 group-hover:text-slate-blue mb-4 transition-colors" />
                             <h3 className="font-bold text-cream uppercase text-sm">Security</h3>
                        </div>
                        <div className="group">
                             <FiZap className="mx-auto text-3xl text-cream/30 group-hover:text-slate-blue mb-4 transition-colors" />
                             <h3 className="font-bold text-cream uppercase text-sm">Speed</h3>
                        </div>
                        <div className="group">
                             <FiTarget className="mx-auto text-3xl text-cream/30 group-hover:text-slate-blue mb-4 transition-colors" />
                             <h3 className="font-bold text-cream uppercase text-sm">Precision</h3>
                        </div>
                        <div className="group">
                             <FiTrendingUp className="mx-auto text-3xl text-cream/30 group-hover:text-slate-blue mb-4 transition-colors" />
                             <h3 className="font-bold text-cream uppercase text-sm">Innovation</h3>
                        </div>
                    </div>
                </Section>

                <section className="py-20 text-center px-4">
                     <h2 className="text-2xl font-black text-cream uppercase tracking-tight mb-8">Ready to start?</h2>
                     <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-block px-10 py-5 bg-cream text-warm-dark text-sm font-black uppercase tracking-widest rounded-full hover:bg-white hover:scale-105 transition-all shadow-[0_0_30px_rgba(235,226,221,0.2)]">
                        Get In Touch
                     </a>
                </section>

            </div>
        </div>
    );
}

export default About;
